import React, { useEffect, useState } from "react";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import SectionCard from "./SectionCard";
import {
  getAllReconGroups,
  getAllSubReconGroups,
} from "../../../api/ap_services";

const APPROVAL_LEVELS = [
  { title: "First Level Approval", prefix: "firstLevel" },
  { title: "Second Level Approval", prefix: "secondLevel" },
  { title: "Third Level Approval", prefix: "thirdLevel" },
  { title: "SAP Team Approval", prefix: "sapTeam" },
  { title: "Finance Team Approval", prefix: "financeTeam" },
];

const LEVEL_APPROVAL_STATUS_OPTIONS = ["PENDING", "REJECTED", "APPROVED"];

const normalizeLevelApprovalStatus = (value) => {
  const normalized = String(value ?? "")
    .trim()
    .toUpperCase();
  if (!normalized || normalized === "N") {
    return "PENDING";
  }
  if (LEVEL_APPROVAL_STATUS_OPTIONS.includes(normalized)) {
    return normalized;
  }
  return "PENDING";
};

const ApprovalLevelSection = ({ title, prefix, formData, setFieldValue }) => {
  const statusField = `${prefix}ApprovalStatus`;
  const statusValue = normalizeLevelApprovalStatus(formData[statusField]);

  useEffect(() => {
    if (formData[statusField] !== statusValue) {
      setFieldValue(statusField, statusValue);
    }
  }, [formData[statusField], statusField, statusValue, setFieldValue]);

  return (
  <SectionCard title={title}>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth size="small">
          <InputLabel id={`${prefix}-approval-status-label`}>
            Approval Status
          </InputLabel>
          <Select
            labelId={`${prefix}-approval-status-label`}
            name={statusField}
            value={statusValue}
            onChange={(e) =>
              setFieldValue(statusField, e.target.value)
            }
            label="Approval Status"
          >
            {LEVEL_APPROVAL_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          label="Approval By"
          name={`${prefix}ApprovalBy`}
          value={formData[`${prefix}ApprovalBy`] || ""}
          onChange={(e) =>
            setFieldValue(`${prefix}ApprovalBy`, e.target.value)
          }
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          label="Approval Date"
          name={`${prefix}ApprovalDate`}
          value={formData[`${prefix}ApprovalDate`] || ""}
          onChange={(e) =>
            setFieldValue(`${prefix}ApprovalDate`, e.target.value)
          }
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          size="small"
          label="Approval Remarks"
          name={`${prefix}ApprovalRemarks`}
          value={formData[`${prefix}ApprovalRemarks`] || ""}
          onChange={(e) =>
            setFieldValue(`${prefix}ApprovalRemarks`, e.target.value)
          }
          multiline
          rows={2}
        />
      </Grid>
    </Grid>
  </SectionCard>
  );
};

const Step5OfficeUse = ({ formData, setFieldValue }) => {
  const [reconGroupOptions, setReconGroupOptions] = useState([]);
  const [subReconGroupOptions, setSubReconGroupOptions] = useState([]);

  useEffect(() => {
    getAllReconGroups()
      .then((res) => {
        const list = res?.response?.responseObject ?? [];
        setReconGroupOptions(Array.isArray(list) ? list : []);
      })
      .catch(() => setReconGroupOptions([]));

    getAllSubReconGroups()
      .then((res) => {
        const list = res?.response?.responseObject ?? [];
        setSubReconGroupOptions(Array.isArray(list) ? list : []);
      })
      .catch(() => setSubReconGroupOptions([]));
  }, []);

  const getOptionValue = (option) =>
    option?.name ?? option?.NAME ?? "";

  const getOptionKey = (option, index) =>
    option?.reconGroupId ??
    option?.RECON_GROUP_ID ??
    option?.subReconGroupId ??
    option?.SUB_RECON_GROUP_ID ??
    getOptionValue(option) ??
    index;

  return (
    <Grid container spacing={2}>
      <SectionCard title="OFFICE USE*">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Withholding Tax Type"
              name="withholdingTaxType"
              value={formData.withholdingTaxType || ""}
              onChange={(e) =>
                setFieldValue("withholdingTaxType", e.target.value)
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Withholding Tax Code"
              name="withholdingTaxCode"
              value={formData.withholdingTaxCode || ""}
              onChange={(e) =>
                setFieldValue("withholdingTaxCode", e.target.value)
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Subject"
              name="subject"
              value={formData.subject || ""}
              onChange={(e) => setFieldValue("subject", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="QsRec"
              name="qsrec"
              value={formData.qsrec || ""}
              onChange={(e) => setFieldValue("qsrec", e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Vendor Code"
              name="vendorCode"
              value={formData.vendorCode || ""}
              onChange={(e) => setFieldValue("vendorCode", e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Vendor Category"
              name="vendorCategory"
              value={formData.vendorCategory || ""}
              onChange={(e) => setFieldValue("vendorCategory", e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="recon-group-label">Recon group</InputLabel>
              <Select
                labelId="recon-group-label"
                name="reconGroup"
                value={formData.reconGroup || ""}
                onChange={(e) => setFieldValue("reconGroup", e.target.value)}
                label="Recon group"
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {reconGroupOptions.map((option, index) => {
                  const value = getOptionValue(option);
                  return (
                    <MenuItem key={getOptionKey(option, index)} value={value}>
                      {value}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="sub-recon-group-label">Sub recon group</InputLabel>
              <Select
                labelId="sub-recon-group-label"
                name="subReconGroup"
                value={formData.subReconGroup || ""}
                onChange={(e) =>
                  setFieldValue("subReconGroup", e.target.value)
                }
                label="Sub recon group"
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {subReconGroupOptions.map((option, index) => {
                  const value = getOptionValue(option);
                  return (
                    <MenuItem key={getOptionKey(option, index)} value={value}>
                      {value}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="brand-label">Brand</InputLabel>
              <Select
                labelId="brand-label"
                name="brand"
                value={formData.brand || ""}
                onChange={(e) => setFieldValue("brand", e.target.value)}
                label="Brand"
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                <MenuItem value="Common">Common</MenuItem>
                <MenuItem value="Levis">Levis</MenuItem>
                <MenuItem value="Dixcy">Dixcy</MenuItem>
                <MenuItem value="Enamor">Enamor</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="approval-status-label">Approval Status</InputLabel>
              <Select
                labelId="approval-status-label"
                name="approvalStatus"
                value={formData.approvalStatus || ""}
                onChange={(e) =>
                  setFieldValue("approvalStatus", e.target.value)
                }
                label="Approval Status"
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Approved">Approved</MenuItem>
                <MenuItem value="Reject">Reject</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={12}>
            <TextField
              fullWidth
              size="small"
              label="Approval Remarks"
              name="approvalRemarks"
              value={formData.approvalRemarks || ""}
              onChange={(e) => setFieldValue("approvalRemarks", e.target.value)}
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </SectionCard>

      {APPROVAL_LEVELS.map((level) => (
        <ApprovalLevelSection
          key={level.prefix}
          title={level.title}
          prefix={level.prefix}
          formData={formData}
          setFieldValue={setFieldValue}
        />
      ))}
    </Grid>
  );
};

export default Step5OfficeUse;
